import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useCallback, useMemo, useState } from "react";

export interface SetSupportTicketSelectedInput {
  supportTicketId: SupportTicketId;
  rowIndex: number;
  checked: boolean;
  shiftKey: boolean;
}

export interface SupportTicketTableSelectionState {
  selectedSupportTicketIds: SupportTicketId[];
  selectedSupportTickets: SupportTicketListItemResponse[];
  selectedSupportTicketCount: number;
  hoveredSupportTicketId: SupportTicketId | null;
  hasAnySelectedSupportTickets: boolean;
  setHoveredSupportTicketId: (id: SupportTicketId | null) => void;
  isSupportTicketSelected: (id: SupportTicketId) => boolean;
  setSupportTicketSelected: (input: SetSupportTicketSelectedInput) => void;
  setAllSupportTicketsSelected: (checked: boolean) => void;
  clearSelectedSupportTickets: () => void;
}

export function useSupportTicketTableSelection(
  conversations: readonly SupportTicketListItemResponse[],
): SupportTicketTableSelectionState {
  const [rawSelectedSupportTicketIds, setRawSelectedSupportTicketIds] =
    useState<SupportTicketId[]>([]);
  const [hoveredSupportTicketId, setHoveredSupportTicketId] =
    useState<SupportTicketId | null>(null);
  const [selectionAnchorIndex, setSelectionAnchorIndex] = useState<
    number | null
  >(null);

  const visibleSupportTicketIdSet = useMemo(
    () => new Set(conversations.map((conversation) => conversation.id)),
    [conversations],
  );

  const selectedSupportTicketIds = useMemo(
    () =>
      rawSelectedSupportTicketIds.filter((id) =>
        visibleSupportTicketIdSet.has(id),
      ),
    [rawSelectedSupportTicketIds, visibleSupportTicketIdSet],
  );

  const selectedSupportTicketIdSet = useMemo(
    () => new Set(selectedSupportTicketIds),
    [selectedSupportTicketIds],
  );

  const selectedSupportTickets = useMemo(
    () =>
      conversations.filter((conversation) =>
        selectedSupportTicketIdSet.has(conversation.id),
      ),
    [conversations, selectedSupportTicketIdSet],
  );

  const selectedSupportTicketCount = selectedSupportTicketIds.length;
  const hasAnySelectedSupportTickets = selectedSupportTicketCount > 0;

  const isSupportTicketSelected = useCallback(
    (id: SupportTicketId) => selectedSupportTicketIdSet.has(id),
    [selectedSupportTicketIdSet],
  );

  const setSupportTicketSelected = useCallback(
    ({
      supportTicketId,
      rowIndex,
      checked,
      shiftKey,
    }: SetSupportTicketSelectedInput) => {
      setRawSelectedSupportTicketIds((previous) => {
        const next = new Set(previous);

        if (shiftKey && selectionAnchorIndex !== null) {
          const startIndex = Math.min(selectionAnchorIndex, rowIndex);
          const endIndex = Math.max(selectionAnchorIndex, rowIndex);
          const rangeIds = conversations
            .slice(startIndex, endIndex + 1)
            .map((conversation) => conversation.id);

          for (const id of rangeIds) {
            if (checked) {
              next.add(id);
            } else {
              next.delete(id);
            }
          }
        } else if (checked) {
          next.add(supportTicketId);
        } else {
          next.delete(supportTicketId);
        }

        return [...next];
      });

      setSelectionAnchorIndex(rowIndex);
    },
    [conversations, selectionAnchorIndex],
  );

  const setAllSupportTicketsSelected = useCallback(
    (checked: boolean) => {
      if (checked) {
        setRawSelectedSupportTicketIds(
          conversations.map((conversation) => conversation.id),
        );
      } else {
        setRawSelectedSupportTicketIds([]);
      }
      setSelectionAnchorIndex(null);
    },
    [conversations],
  );

  const clearSelectedSupportTickets = useCallback(() => {
    setRawSelectedSupportTicketIds([]);
    setSelectionAnchorIndex(null);
  }, []);

  return {
    selectedSupportTicketIds,
    selectedSupportTickets,
    selectedSupportTicketCount,
    hoveredSupportTicketId,
    hasAnySelectedSupportTickets,
    setHoveredSupportTicketId,
    isSupportTicketSelected,
    setSupportTicketSelected,
    setAllSupportTicketsSelected,
    clearSelectedSupportTickets,
  };
}
